import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Swiper as SwiperType } from 'swiper';
import GridSlider from '../../../components/Slider/GridSlider';
import Intro from './section-intro';
import Certified from './section-certified';
import Service from './section-services';
import Values from './section-values';
import About from './section-about';
import Contact from './section-contact';
import LoaderTooltip from '../../../components/Loader/TooltipLoader';

interface HeaderProps {
  setSwiper: (swiper: SwiperType | null) => void;
}

const Header: React.FC<HeaderProps> = ({ setSwiper }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const hideTimeoutRef = useRef<number | null>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const triggerTooltip = () => {
    setShowTooltip(true);

    if (hideTimeoutRef.current) {
      window.clearTimeout(hideTimeoutRef.current);
    }

    hideTimeoutRef.current = window.setTimeout(() => {
      setShowTooltip(false);
    }, 1000);
  };

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) > 5) {
        triggerTooltip();
      }
    };

    // 📱 Mobile — vertical only
    const handleTouchStart = (e: TouchEvent) => {
      touchStart.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchStart.current) return;

      const deltaX = e.touches[0].clientX - touchStart.current.x;
      const deltaY = e.touches[0].clientY - touchStart.current.y;

      // Only vertical scroll
      if (Math.abs(deltaY) > 10 && Math.abs(deltaY) > Math.abs(deltaX)) {
        triggerTooltip();
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  return (
    <header className="header-container">
      {showTooltip && createPortal(<LoaderTooltip />, document.body)}

      <GridSlider slidesPerView="auto" spaceBetween={24} centered onReady={setSwiper}>
        <Intro />
        <Certified />
        <Service />
        <Values />
        <About />
        <Contact />
      </GridSlider>
    </header>
  );
};

export default Header;
